import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { CopyableSecret } from "../../components/ui/CopyableSecret";
import {
  useAttachPage,
  useCreateWorkspace,
  useDiscoverPages,
  useExchangeToken,
  useLeadForms,
  useSubscribePage,
  type DiscoveredPage,
} from "../../features/superadmin/api";

type Step = "profile" | "credentials" | "pages" | "forms" | "done";

interface State {
  slug: string;
  name: string;
  appId: string;
  appSecret: string;
  shortToken: string;
  longToken: string;
  pages: DiscoveredPage[];
  pickedPageIds: string[];
  workspaceId: string | null;
  formsApiKey: string | null;
  /** verify tokens returned by attach, keyed by Meta page_id */
  verifyTokens: Record<string, string>;
}

const INITIAL: State = {
  slug: "",
  name: "",
  appId: "",
  appSecret: "",
  shortToken: "",
  longToken: "",
  pages: [],
  pickedPageIds: [],
  workspaceId: null,
  formsApiKey: null,
  verifyTokens: {},
};

// Survives accidental refreshes mid-wizard. Cleared on Done / on the Open
// Workspace button so the next add starts fresh.
const STORAGE_KEY = "ml.add-workspace-wizard.v1";

interface Persisted {
  step: Step;
  state: State;
}

function loadPersisted(): Persisted | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Persisted;
    return parsed;
  } catch {
    return null;
  }
}

function clearPersisted(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage may be disabled in some browser modes — ignore.
  }
}

export default function AddWorkspacePage() {
  const initial = loadPersisted();
  const [step, setStep] = useState<Step>(initial?.step ?? "profile");
  const [state, setState] = useState<State>({
    ...INITIAL,
    ...(initial?.state ?? {}),
  });
  const navigate = useNavigate();

  // Persist after every change so a refresh mid-flow resumes where we left off.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ step, state } satisfies Persisted)
      );
    } catch {
      // ignore quota/disabled-storage errors
    }
  }, [step, state]);

  const create = useCreateWorkspace();
  const exchange = useExchangeToken();
  const discover = useDiscoverPages();

  return (
    <Card title="Add workspace" subtitle="6-step wizard for connecting a Meta App + Pages.">
      <Stepper step={step} />

      {step === "profile" && (
        <form
          className="form-stack"
          onSubmit={async (e) => {
            e.preventDefault();
            setStep("credentials");
          }}
        >
          <Input
            label="Workspace name"
            value={state.name}
            onChange={(e) => setState({ ...state, name: e.target.value })}
            required
          />
          <Input
            label="Slug (lowercase, hyphens)"
            value={state.slug}
            onChange={(e) => setState({ ...state, slug: e.target.value.toLowerCase().replace(/\s+/g, "-") })}
            pattern="[a-z0-9-]+"
            required
          />
          <div className="dialog-actions">
            <Button type="submit">Next →</Button>
          </div>
        </form>
      )}

      {step === "credentials" && (
        <form
          className="form-stack"
          onSubmit={async (e) => {
            e.preventDefault();
            const created = await create.mutateAsync({
              slug: state.slug,
              name: state.name,
              metaAppId: state.appId,
              metaAppSecret: state.appSecret,
            });
            setState({ ...state, workspaceId: created.workspace.id, formsApiKey: created.formsApiKey });
            const tok = await exchange.mutateAsync({
              app_id: state.appId,
              app_secret: state.appSecret,
              short_token: state.shortToken,
            });
            setState((s) => ({ ...s, longToken: tok.long_lived_token, workspaceId: created.workspace.id, formsApiKey: created.formsApiKey }));
            setStep("pages");
          }}
        >
          <Input label="Meta App ID" value={state.appId} onChange={(e) => setState({ ...state, appId: e.target.value })} required />
          <Input label="Meta App Secret" type="password" value={state.appSecret} onChange={(e) => setState({ ...state, appSecret: e.target.value })} required />
          <Input
            label="Short-lived user token (from Graph Explorer)"
            value={state.shortToken}
            onChange={(e) => setState({ ...state, shortToken: e.target.value })}
            required
          />
          {(create.isError || exchange.isError) ? (
            <div className="form-error">
              {(create.error as Error)?.message ?? (exchange.error as Error)?.message}
            </div>
          ) : null}
          <div className="dialog-actions">
            <Button type="button" variant="ghost" onClick={() => setStep("profile")}>← Back</Button>
            <Button type="submit" disabled={create.isPending || exchange.isPending}>
              {create.isPending || exchange.isPending ? "Working…" : "Create + exchange →"}
            </Button>
          </div>
        </form>
      )}

      {step === "pages" && (
        <PagesStep
          state={state}
          setState={setState}
          discover={discover}
          onBack={() => setStep("credentials")}
          onNext={() => setStep("forms")}
        />
      )}

      {step === "forms" && (
        <FormsStep
          state={state}
          setState={setState}
          onBack={() => setStep("pages")}
          onDone={() => setStep("done")}
        />
      )}

      {step === "done" && (
        <div className="form-stack">
          <div className="form-info">
            ✅ Workspace <strong>{state.name}</strong> is live.
          </div>
          <dl className="detail-list">
            <dt>Forms API key</dt>
            <dd><CopyableSecret value={state.formsApiKey} /></dd>
            <dt>Webhook URL</dt>
            <dd><CopyableSecret value={`/webhook/${state.slug}`} /></dd>
          </dl>
          <div className="dialog-actions">
            <Button
              onClick={() => {
                clearPersisted();
                navigate(`/superadmin/${state.workspaceId}`);
              }}
            >
              Open workspace
            </Button>
          </div>
        </div>
      )}
      <p className="muted" style={{ marginTop: 14 }}>
        <Link to="/superadmin" className="link">← Back to workspaces</Link>
      </p>
    </Card>
  );
}

function Stepper({ step }: { step: Step }) {
  const steps: Step[] = ["profile", "credentials", "pages", "forms", "done"];
  const labels: Record<Step, string> = {
    profile: "Profile",
    credentials: "Credentials",
    pages: "Pages",
    forms: "Forms",
    done: "Done",
  };
  const idx = steps.indexOf(step);
  return (
    <ol className="stepper">
      {steps.map((s, i) => (
        <li key={s} className={`stepper-item ${i <= idx ? "is-active" : ""}`.trim()}>
          <span className="stepper-num">{i + 1}</span>
          <span>{labels[s]}</span>
        </li>
      ))}
    </ol>
  );
}

interface PagesStepProps {
  state: State;
  setState: (s: State) => void;
  discover: ReturnType<typeof useDiscoverPages>;
  onBack: () => void;
  onNext: () => void;
}

function PagesStep({ state, setState, discover, onBack, onNext }: PagesStepProps) {
  const attach = useAttachPage(state.workspaceId ?? "");
  const subscribe = useSubscribePage();
  const [busy, setBusy] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  return (
    <div className="form-stack">
      <Button
        type="button"
        onClick={async () => {
          const res = await discover.mutateAsync({ long_lived_token: state.longToken });
          setState({ ...state, pages: res.pages });
        }}
        disabled={discover.isPending}
      >
        {discover.isPending ? "Discovering…" : "Discover pages"}
      </Button>
      {discover.isError ? <div className="form-error">{(discover.error as Error).message}</div> : null}

      {state.pages.length > 0 && (
        <div className="checklist">
          {state.pages.map((p) => (
            <label key={p.id} className="checklist-item">
              <input
                type="checkbox"
                checked={state.pickedPageIds.includes(p.id)}
                onChange={(e) => {
                  const set = new Set(state.pickedPageIds);
                  if (e.target.checked) set.add(p.id); else set.delete(p.id);
                  setState({ ...state, pickedPageIds: Array.from(set) });
                }}
              />
              <div>
                <div className="checklist-title">{p.name}</div>
                <div className="checklist-sub">{p.id} · via {p._via ?? "—"}</div>
              </div>
            </label>
          ))}
        </div>
      )}

      {Object.keys(state.verifyTokens).length > 0 ? (
        <div className="form-stack" style={{ marginTop: 8 }}>
          <p className="muted">
            Paste each verify token into Meta's "Webhook → Verify token" field
            when subscribing this app to the corresponding page:
          </p>
          <dl className="detail-list">
            {state.pickedPageIds.flatMap((pid) => {
              const token = state.verifyTokens[pid];
              if (!token) return [];
              const page = state.pages.find((p) => p.id === pid);
              return [
                <>
                  <dt key={`${pid}-label`}>{page?.name ?? pid}</dt>
                  <dd key={`${pid}-val`}>
                    <CopyableSecret value={token} />
                  </dd>
                </>,
              ];
            })}
          </dl>
        </div>
      ) : null}

      {attachError ? <div className="form-error">{attachError}</div> : null}

      <div className="dialog-actions">
        <Button type="button" variant="ghost" onClick={onBack}>← Back</Button>
        <Button
          type="button"
          disabled={state.pickedPageIds.length === 0 || busy}
          onClick={async () => {
            if (!state.workspaceId) {
              setAttachError("Missing workspace id — go back to the credentials step.");
              return;
            }
            setBusy(true);
            setAttachError(null);
            try {
              const verifyTokens: Record<string, string> = { ...state.verifyTokens };
              for (const pageId of state.pickedPageIds) {
                const page = state.pages.find((p) => p.id === pageId);
                if (!page) continue;
                const attached = await attach.mutateAsync({
                  pageId: page.id,
                  pageName: page.name,
                  pageAccessToken: page.access_token,
                  formIds: [],
                });
                verifyTokens[page.id] = attached.page.verifyToken;
                await subscribe.mutateAsync({
                  page_id: page.id,
                  page_access_token: page.access_token,
                });
              }
              setState({ ...state, verifyTokens });
              onNext();
            } catch (err) {
              setAttachError(
                `Failed while attaching/subscribing pages: ${(err as Error).message}. ` +
                  "Some pages may have been partially saved — review the workspace detail page."
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Attaching…" : "Attach + subscribe →"}
        </Button>
      </div>
    </div>
  );
}

interface FormsStepProps {
  state: State;
  setState: (s: State) => void;
  onBack: () => void;
  onDone: () => void;
}

function FormsStep({ state, setState, onBack, onDone }: FormsStepProps) {
  const pickedPages = useMemo(
    () => state.pages.filter((p) => state.pickedPageIds.includes(p.id)),
    [state.pages, state.pickedPageIds]
  );
  const [pageIdx, setPageIdx] = useState(0);
  const current = pickedPages[pageIdx];
  const { data, isLoading } = useLeadForms(current?.id ?? null, current?.access_token ?? null);
  const attach = useAttachPage(state.workspaceId ?? "");
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [finishError, setFinishError] = useState<string | null>(null);

  if (!current) {
    return (
      <div className="form-stack">
        <p className="muted">No pages picked — skip ahead to finish.</p>
        <div className="dialog-actions">
          <Button type="button" variant="ghost" onClick={onBack}>← Back</Button>
          <Button onClick={onDone}>Finish →</Button>
        </div>
      </div>
    );
  }

  const togglesForCurrent = picked[current.id] ?? [];
  const setTogglesForCurrent = (next: string[]) =>
    setPicked((p) => ({ ...p, [current.id]: next }));

  return (
    <div className="form-stack">
      <p className="muted">
        Pick which lead forms feed <strong>{current.name}</strong> ({pageIdx + 1}/{pickedPages.length}) into this workspace.
      </p>
      {isLoading ? (
        <div className="skeleton-rows">Loading forms…</div>
      ) : (
        <div className="checklist">
          {(data?.forms ?? []).map((f) => (
            <label key={f.id} className="checklist-item">
              <input
                type="checkbox"
                checked={togglesForCurrent.includes(f.id)}
                onChange={(e) => {
                  const set = new Set(togglesForCurrent);
                  if (e.target.checked) set.add(f.id); else set.delete(f.id);
                  setTogglesForCurrent(Array.from(set));
                }}
              />
              <div>
                <div className="checklist-title">{f.name}</div>
                <div className="checklist-sub">
                  {f.id} {f.leads_count != null ? `· ${f.leads_count} leads` : ""}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}

      {finishError ? <div className="form-error">{finishError}</div> : null}

      <div className="dialog-actions">
        <Button type="button" variant="ghost" onClick={onBack}>← Back</Button>
        {pageIdx + 1 < pickedPages.length ? (
          <Button onClick={() => setPageIdx((i) => i + 1)}>Next page →</Button>
        ) : (
          <Button
            onClick={async () => {
              if (!state.workspaceId) {
                setFinishError("Missing workspace id — go back to the credentials step.");
                return;
              }
              setFinishError(null);
              try {
                for (const page of pickedPages) {
                  await attach.mutateAsync({
                    pageId: page.id,
                    pageName: page.name,
                    pageAccessToken: page.access_token,
                    formIds: picked[page.id] ?? [],
                  });
                }
                setState({ ...state });
                onDone();
              } catch (err) {
                setFinishError(
                  `Failed while saving form selections: ${(err as Error).message}.`
                );
              }
            }}
            disabled={attach.isPending}
          >
            {attach.isPending ? "Saving…" : "Finish →"}
          </Button>
        )}
      </div>
    </div>
  );
}
